/**
 * GxLineChart: categorical x, numeric y, optional shaded band between two series, dashed thresholds ("Your floors") and
 * end-of-line value labels. Sized to its container (the parent measures the width). A series may turn dashed from one category on
 * (`dashFrom`: a projection after the realised path), a `range` shades a low-high band behind it and a `rule` marks a category ("Today").
 */
import { useEffect, useState, type ReactNode } from "react";
import { insetLabel, labelSize, labelWidth } from "../charts/labels";
import { ChartFrame, type ChartTable } from "./ChartFrame";

export interface LineSeries {
  readonly key: string; readonly color: string; readonly labelColor: string; readonly values: readonly number[];
  /** What the line is called: shown on hover over the line and each point. */
  readonly name?: string;
  /** A short name printed before the end value, so a line is identified where it ends (the five rivals in the finale chart). */
  readonly endLabel?: string;
  /** From this category index on the line is dashed and its points are invisible hover targets (a projection); the points before it are not drawn. */
  readonly dashFrom?: number;
}
/** Small marks that sit on the categories without a line between them (other banks' year-end readings); `null` = no mark at that category. */
export interface Marks { readonly key: string; readonly name: string; /** Names the mark's last reading at the right edge, beside the end labels (a name only, no value). */ readonly endLabel?: string; readonly color: string; readonly values: readonly (number | null)[] }
/** A shaded band that starts at category `start`: `low` and `high` hold one value per category from there. */
export interface Range { readonly start: number; readonly low: readonly number[]; readonly high: readonly number[]; readonly color: string }
export interface Threshold { readonly value: number; readonly label: string; /** The line and its name; default the neutral ink. */ readonly color?: string }

const PAD = { left: 40, right: 74, top: 16, bottom: 32 } as const;
/** Width of one character of an end label, to size the right gutter for the longest threshold name. */
const LABEL_CHAR_W = 6.4;
/** Least vertical distance between two end labels (11.5px type). */
const LABEL_GAP = 17;
/** Category names stand at least this far apart (their measured width plus this). */
const CAT_GAP = 8;
/** Width of one character of a rule's name (10.5px type), to flip the name to the rule's right when the left has no room. */
const RULE_CHAR_W = 6;
/** Under this width (a phone card is about 358 px) the axes thin out: every other category name, at most four y ticks. */
export const THIN_BELOW = 480;
const MAX_Y_TICKS_THIN = 4;
/** The ticks drawn: all of them, or on a narrow chart four spread evenly (the first and last always stay). Pure. */
export function thinTicks(ticks: readonly number[], thin: boolean): readonly number[] {
  if (!thin || ticks.length <= MAX_Y_TICKS_THIN) return ticks;
  const pick = new Set(Array.from({ length: MAX_Y_TICKS_THIN }, (_, k) => Math.round((k * (ticks.length - 1)) / (MAX_Y_TICKS_THIN - 1))));
  return ticks.filter((_, i) => pick.has(i));
}

/**
 * Pushes labels (sorted by `at`) apart to at least `gap` and keeps them inside [min, max]. A `pinned` label (a threshold's name, which must stand
 * on its own line) never moves: the free labels between two pinned ones (or an edge) form a group, and each group is spread about where it belongs
 * (the least-squares spread, by pooling adjacent labels that collide) inside the room the pinned labels leave it, so a value that sits within a
 * label height of the twin line steps aside while the twin's name stays on its line.
 */
export function spreadLabels(labels: { at: number; pinned?: boolean }[], gap: number, min: number, max: number): void {
  let from = 0;
  let floor = min;
  for (let i = 0; i <= labels.length; i += 1) {
    const edge = labels[i];
    if (edge !== undefined && edge.pinned !== true) continue;
    spreadGroup(labels.slice(from, i), gap, floor, edge === undefined ? max : edge.at - gap);
    from = i + 1;
    floor = edge === undefined ? floor : edge.at + gap;
  }
}

/** One run of free labels into [floor, ceiling] (the ceiling is the last label's top): pool adjacent clashes, then clamp the pooled blocks into the room. */
function spreadGroup(group: { at: number }[], gap: number, floor: number, ceiling: number): void {
  // Label i sits at q_i + i·gap, so "at least a gap apart" is "q never decreases": the isotonic fit of q to the wanted positions.
  const blocks: { sum: number; n: number; q: number }[] = [];
  group.forEach((l, i) => {
    blocks.push({ sum: l.at - i * gap, n: 1, q: l.at - i * gap });
    for (let k = blocks.length - 1; k > 0 && (blocks[k - 1] as { q: number }).q > (blocks[k] as { q: number }).q; k -= 1) {
      const top = blocks.pop() as { sum: number; n: number }, below = blocks[k - 1] as { sum: number; n: number; q: number };
      below.sum += top.sum; below.n += top.n; below.q = below.sum / below.n;
    }
  });
  const room = Math.max(floor, ceiling - (group.length - 1) * gap);
  let i = 0;
  for (const b of blocks) for (let k = 0; k < b.n; k += 1, i += 1) (group[i] as { at: number }).at = Math.min(Math.max(b.q, floor), room) + i * gap;
}

/** A point of a drawn line, in pixels. */
type Pt = { readonly x: number; readonly y: number };
const PILL_H = 22;
const PILL_CHAR_W = 6.6;

/**
 * Where a note pill (the "gap 150bp" figure) stands: as near its wanted centre as it can without covering a line, a floor, the rule or a mark.
 * `lines` are the drawn polylines (points in pixels); `hLines` the y of each full-width floor; `vLines` the x of each rule; `dots` the single marks.
 * Falls back to the wanted centre when the plot has no free room.
 */
export function placePill(want: Pt, size: { readonly w: number; readonly h: number }, box: { readonly left: number; readonly right: number; readonly top: number; readonly bottom: number },
  lines: readonly (readonly Pt[])[], hLines: readonly number[], vLines: readonly number[], dots: readonly Pt[]): Pt {
  const pad = 4;
  const samples: Pt[] = [...dots];
  for (const line of lines) {
    line.forEach((a, i) => {
      samples.push(a);
      const b = line[i + 1];
      if (b === undefined) return;
      const n = Math.ceil(Math.hypot(b.x - a.x, b.y - a.y) / 3);
      for (let k = 1; k < n; k += 1) samples.push({ x: a.x + ((b.x - a.x) * k) / n, y: a.y + ((b.y - a.y) * k) / n });
    });
  }
  const free = (c: Pt): boolean => {
    const x0 = c.x - size.w / 2 - pad, x1 = c.x + size.w / 2 + pad, y0 = c.y - size.h / 2 - pad, y1 = c.y + size.h / 2 + pad;
    return !samples.some((s) => s.x >= x0 && s.x <= x1 && s.y >= y0 && s.y <= y1) && !hLines.some((h) => h >= y0 && h <= y1) && !vLines.some((v) => v >= x0 && v <= x1);
  };
  if (free(want)) return want;
  let best: Pt | null = null;
  let bestD = Infinity;
  for (let cy = box.top + size.h / 2; cy <= box.bottom - size.h / 2; cy += 4) {
    for (let cx = box.left + size.w / 2; cx <= box.right - size.w / 2; cx += 8) {
      const d = Math.hypot((cx - want.x) / 2, cy - want.y);
      if (d < bestD && free({ x: cx, y: cy })) { best = { x: cx, y: cy }; bestD = d; }
    }
  }
  return best ?? want;
}

export function GxLineChart({ width, height: heightIn, categories, series, marks = [], band, thresholds, yTicks, formatY, formatEnd, annotation, range, rule, labelled, axisLabels, summary, table, testId }: {
  readonly width: number; readonly height: number; readonly categories: readonly string[]; readonly series: readonly LineSeries[];
  /** Small marks beside the lines (no line joins them): each mark reads as its name and value on hover. */
  readonly marks?: readonly Marks[];
  /** Keys of the upper and lower series; the gap between them is shaded in the lower series' colour. */
  readonly band?: { readonly upper: string; readonly lower: string };
  readonly thresholds: readonly Threshold[]; readonly yTicks: readonly number[];
  readonly formatY: (v: number) => string; readonly formatEnd: (v: number) => string;
  readonly annotation?: { readonly text: string; readonly at: number; readonly value: number };
  readonly range?: Range;
  /** A vertical rule at category `at`, named at the top. */
  readonly rule?: { readonly at: number; readonly label: string };
  /** Which category labels are drawn (default all); the last always is. */
  readonly labelled?: (index: number) => boolean;
  /** Plain names for the axes: what the y axis measures with its unit, and what the x axis counts. */
  readonly axisLabels?: { readonly x?: string; readonly y?: string };
  readonly summary: string; readonly table: ChartTable; readonly testId: string;
}) {
  const thin = width < THIN_BELOW;
  // A tap names a point (a touch screen has no hover): it opens a note, a tap elsewhere or a scroll closes it.
  const [tip, setTip] = useState<{ readonly x: number; readonly y: number; readonly text: string } | null>(null);
  useEffect(() => {
    if (tip === null) return;
    const close = () => setTip(null);
    window.addEventListener("scroll", close, { passive: true, once: true });
    return () => window.removeEventListener("scroll", close);
  }, [tip]);
  const all = [...series.flatMap((s) => s.values), ...marks.flatMap((m) => m.values.filter((v): v is number => v !== null)), ...(range === undefined ? [] : [...range.low, ...range.high]), ...thresholds.map((t) => t.value), ...yTicks];
  const lo = Math.min(...all) - 0.3;
  const hi = Math.max(...all) + 0.3;
  const last = categories.length - 1;
  // A category may carry a line break ("Y5\nlook-through"): long labels stack instead of overprinting their neighbours.
  const stacked = categories.some((c) => c.includes("\n"));
  const xLabelH = axisLabels?.x === undefined ? 0 : 16;
  const padBottom = (stacked ? PAD.bottom + 14 : PAD.bottom) + xLabelH;
  // Every end label (and the threshold's name) needs a gap of its own: a chart too short to hold them all grows until they do, rather than letting a crowd overprint the twin's name.
  const endValues = [...series.map((s) => ({ v: s.values[last] ?? 0, pinned: false })), ...marks.flatMap((m) => { const v = m.values[last]; return m.endLabel === undefined || v === null || v === undefined ? [] : [{ v, pinned: false }]; }), ...thresholds.map((t) => ({ v: t.value, pinned: true }))];
  const fitsAt = (h: number): boolean => {
    const items = endValues.map((e) => ({ at: h - padBottom - ((e.v - lo) / (hi - lo)) * (h - padBottom - PAD.top), pinned: e.pinned })).sort((p, q) => p.at - q.at);
    spreadLabels(items, LABEL_GAP, PAD.top, h - padBottom);
    return items.every((it, i) => i === 0 || it.at - (items[i - 1]?.at ?? 0) >= LABEL_GAP - 0.01);
  };
  let height = heightIn;
  for (let k = 0; k < 20 && !fitsAt(height); k += 1) height += 10;
  // Threshold names stand in the right gutter beside the end values (staggered with them), so the right pad fits the longest one.
  const padRight = Math.max(PAD.right, 34 + LABEL_CHAR_W * Math.max(0, ...thresholds.map((t) => t.label.length)), 34 + LABEL_CHAR_W * Math.max(0, ...series.map((s) => (s.endLabel === undefined ? 0 : s.endLabel.length + 12)), ...marks.map((m) => (m.endLabel === undefined || m.values[last] == null ? 0 : m.endLabel.length + 1))));
  const plotRight = width - padRight;
  // The y labels are right-aligned to the axis: widen the left pad to the longest one so "−10.0pp" is never clipped.
  const padLeft = Math.max(PAD.left, 14 + 6.5 * Math.max(0, ...yTicks.map((v) => formatY(v).length))) + (axisLabels?.y === undefined ? 0 : 16);
  // A lone point stands at the right edge, beside its end labels, so a floor line never runs through a label.
  const x = (i: number) => (last === 0 ? plotRight : padLeft + 8 + (i / last) * (plotRight - padLeft - 8));
  const y = (v: number) => height - padBottom - ((v - lo) / (hi - lo)) * (height - padBottom - PAD.top);
  // Category names are drawn left to right and never over a neighbour: the last always is, and a named one that would touch it (or the one before) is left to the table.
  const shown = new Set<number>();
  const catFs = labelSize(11, width), longestOf = (c: string): string => c.split("\n").reduce((a, l) => (l.length > a.length ? l : a), "");
  const catAt = (i: number): number => insetLabel(x(i), longestOf(categories[i] ?? ""), catFs, width, "middle", i === last ? 600 : 500).x;
  const halfW = (i: number): number => labelWidth(longestOf(categories[i] ?? ""), catFs, i === last ? 600 : 500) / 2;
  const touches = (a: number, b: number): boolean => catAt(b) - halfW(b) < catAt(a) + halfW(a) + CAT_GAP;
  let lastDrawn = -1;
  categories.forEach((_, i) => {
    if (i === last || (labelled !== undefined && !labelled(i)) || (thin && (last - i) % 2 !== 0)) return;
    if ((lastDrawn < 0 || !touches(lastDrawn, i)) && !touches(i, last)) { shown.add(i); lastDrawn = i; }
  });
  shown.add(last);
  const path = (values: readonly number[]) => values.map((v, i) => `${i === 0 ? "M" : "L"}${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = range === undefined ? null : [
    ...range.high.map((v, i) => `${i === 0 ? "M" : "L"}${x(range.start + i).toFixed(1)},${y(v).toFixed(1)}`),
    ...range.low.map((v, i) => `L${x(range.start + i).toFixed(1)},${y(v).toFixed(1)}`).reverse(), "Z",
  ].join(" ");
  const upper = series.find((s) => s.key === band?.upper);
  const lower = series.find((s) => s.key === band?.lower);
  // End labels (series values and threshold names alike) sit at their line's last point; a crowd is spread evenly about where it belongs
  // rather than stacked downward, so no label drifts far from its line and none overprints another.
  const ends = [
    ...series.map((s) => ({ key: s.key, text: `${s.endLabel === undefined ? "" : `${s.endLabel} `}${formatEnd(s.values[last] ?? 0)}`, color: s.labelColor, weight: 700, italic: false, pinned: false, from: y(s.values[last] ?? 0), at: y(s.values[last] ?? 0) })),
    ...marks.flatMap((m) => { const v = m.values[last]; return m.endLabel === undefined || v === null || v === undefined ? [] : [{ key: `mark-${m.key}`, text: m.endLabel, color: "var(--ink-3)", weight: 500, italic: false, pinned: false, from: y(v), at: y(v) }]; }),
    ...thresholds.map((t) => ({ key: `threshold-${t.label}`, text: t.label, color: t.color ?? "var(--ink-2)", weight: 600, italic: true, pinned: true, from: y(t.value), at: y(t.value) })),
  ].sort((a, b) => a.at - b.at);
  spreadLabels(ends, LABEL_GAP, PAD.top, height - padBottom);
  const endLabels: ReactNode[] = ends.map(({ key, text, color, weight, italic, from, at }) => (
    <g key={key}>
      {Math.abs(at - from) > 3 ? <polyline points={`${x(last) + 4},${from} ${x(last) + 9},${from} ${x(last) + 14},${at} ${x(last) + 17},${at}`} fill="none" stroke="var(--ink-3)" strokeWidth="1" /> : null}
      <text x={insetLabel(x(last) + 20, text, labelSize(11.5, width), width, "start", weight).x} y={at + 4} fontSize={labelSize(11.5, width)} fontWeight={weight} fontStyle={italic ? "italic" : "normal"} fill={color}>{text}</text>
    </g>
  ));
  // The note sits in the free room nearest the gap it describes, never over a line, floor, rule or mark.
  const pillW = annotation === undefined ? 0 : Math.ceil(annotation.text.length * PILL_CHAR_W) + 20;
  const from = annotation === undefined ? { x: 0, y: 0 } : { x: x(annotation.at), y: y(annotation.value) };
  const pill = annotation === undefined ? null : {
    w: pillW, from,
    ...placePill(from, { w: pillW, h: PILL_H }, { left: padLeft, right: plotRight, top: PAD.top, bottom: height - padBottom },
      [...series.map((s) => s.values.map((v, i) => ({ x: x(i), y: y(v) })))],
      thresholds.map((t) => y(t.value)), rule === undefined ? [] : [x(rule.at)],
      marks.flatMap((m) => m.values.flatMap((v, i) => (v === null ? [] : [{ x: x(i), y: y(v) }])))),
  };
  return (
    <ChartFrame summary={summary} table={table}>
      <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} style={{ display: "block" }} data-testid={testId} data-thin={thin ? "" : undefined} onClick={() => setTip(null)}>
        {thinTicks(yTicks, thin).map((v) => (
          <g key={v}>
            <line x1={padLeft} x2={plotRight} y1={y(v)} y2={y(v)} stroke="var(--grid)" />
            <text x={insetLabel(padLeft - 10, formatY(v), labelSize(11, width), width, "end").x} y={y(v) + 4} fontSize={labelSize(11, width)} textAnchor="end" fill="var(--ink-3)">{formatY(v)}</text>
          </g>
        ))}
        {categories.map((c, i) => {
          if (!shown.has(i)) return null;
          const fs = labelSize(11, width), longest = c.split("\n").reduce((a, l) => (l.length > a.length ? l : a), ""), cx = insetLabel(x(i), longest, fs, width, "middle", i === last ? 600 : 500).x;
          return (
            <text key={c} x={cx} y={height - 10 - xLabelH - (c.split("\n").length - 1) * 13} fontSize={fs} textAnchor="middle" fill="var(--ink-3)" fontWeight={i === last ? 600 : 500}>
              {c.split("\n").map((line, k) => <tspan key={line} x={cx} dy={k === 0 ? 0 : 13}>{line}</tspan>)}
            </text>
          );
        })}
        {axisLabels?.x === undefined ? null : <text x={insetLabel((padLeft + plotRight) / 2, axisLabels.x, labelSize(11, width), width).x} y={height - 3} fontSize={labelSize(11, width)} textAnchor="middle" fill="var(--ink-2)" data-testid={`${testId}.xlabel`}>{axisLabels.x}</text>}
        {axisLabels?.y === undefined ? null : <text transform={`translate(11,${(PAD.top + height - padBottom) / 2}) rotate(-90)`} fontSize={labelSize(11, width)} textAnchor="middle" fill="var(--ink-2)" data-testid={`${testId}.ylabel`}>{axisLabels.y}</text>}
        {thresholds.map((t) => <line key={t.label} x1={padLeft} x2={plotRight} y1={y(t.value)} y2={y(t.value)} stroke={t.color ?? "var(--ink-3)"} strokeDasharray="5 4" strokeWidth="1.2" opacity=".85" />)}
        {area === null || range === undefined ? null : <path d={area} fill={range.color} opacity=".14" />}
        {rule === undefined ? null : (
          <g>
            <line x1={x(rule.at)} x2={x(rule.at)} y1={PAD.top - 4} y2={height - padBottom} stroke="var(--ink-3)" strokeDasharray="2 3" />
            {rule.label === "" ? null : <text x={x(rule.at) + (x(rule.at) - 4 - padLeft < RULE_CHAR_W * rule.label.length ? 4 : -4)} y={PAD.top + 6} fontSize={labelSize(10.5, width)} fontWeight="600" textAnchor={x(rule.at) - 4 - padLeft < RULE_CHAR_W * rule.label.length ? "start" : "end"} fill="var(--ink-3)">{rule.label}</text>}
          </g>
        )}
        {upper === undefined || lower === undefined ? null : (
          <path d={`${path(upper.values)} L${lower.values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).reverse().join(" L")} Z`} fill={lower.color} opacity=".10" />
        )}
        {series.map((s) => {
          const split = s.dashFrom;
          const solid = split === undefined ? s.values : s.values.slice(0, split + 1);
          return (
            <g key={s.key}>
              {s.name === undefined ? null : <title>{s.name}</title>}
              <path d={path(solid)} fill="none" stroke={s.color} strokeWidth="2.25" strokeLinejoin="round" />
              {split === undefined ? null : <path d={s.values.slice(split).map((v, i) => `${i === 0 ? "M" : "L"}${x(split + i).toFixed(1)},${y(v).toFixed(1)}`).join(" ")} fill="none" stroke={s.color} strokeWidth="2.25" strokeLinejoin="round" strokeDasharray="6 4" />}
              {s.name === undefined ? null : <path d={path(s.values)} fill="none" stroke="transparent" strokeWidth="12" pointerEvents="stroke" />}
              {s.values.map((v, i) => { if (split !== undefined && i <= split) return null; const text = s.name === undefined ? undefined : `${s.name} · ${(categories[i] ?? "").replace("\n", " ")}: ${formatEnd(v)}`; return <g key={i}><circle cx={x(i)} cy={y(v)} r={split === undefined ? 3.2 : 5} fill={split === undefined ? "var(--surface)" : "transparent"} stroke={split === undefined ? s.color : "none"} strokeWidth="2">{text === undefined ? null : <title>{text}</title>}</circle>{text === undefined ? null : <rect x={x(i) - 14} y={y(v) - 14} width="28" height="28" rx="14" fill="transparent" data-tap="" onClick={(e) => { e.stopPropagation(); setTip({ x: x(i), y: y(v), text }); }} />}</g>; })}
            </g>
          );
        })}
        {marks.map((m) => (
          <g key={m.key}>
            {m.values.map((v, i) => { if (v === null) return null; const text = `${m.name} · ${(categories[i] ?? "").replace("\n", " ")}: ${formatEnd(v)}`; return <g key={i}><circle cx={x(i)} cy={y(v)} r="3" fill={m.color} opacity=".7"><title>{text}</title></circle><rect x={x(i) - 14} y={y(v) - 14} width="28" height="28" rx="14" fill="transparent" data-tap="" onClick={(e) => { e.stopPropagation(); setTip({ x: x(i), y: y(v), text }); }} /></g>; })}
          </g>
        ))}
        {endLabels}
        {tip === null ? null : (
          <g data-testid={`${testId}.tip`} pointerEvents="none">
            <rect x={Math.min(Math.max(tip.x - (tip.text.length * 3.4 + 8), 2), width - (tip.text.length * 6.8 + 16) - 2)} y={Math.max(tip.y - 38, 2)} width={tip.text.length * 6.8 + 16} height={26} rx="6" fill="var(--ink)" />
            <text x={Math.min(Math.max(tip.x - (tip.text.length * 3.4 + 8), 2), width - (tip.text.length * 6.8 + 16) - 2) + 8} y={Math.max(tip.y - 38, 2) + 17} fontSize="12" fill="var(--surface)">{tip.text}</text>
          </g>
        )}
        {pill === null || annotation === undefined ? null : (
          <g data-testid={`${testId}.note`}>
            {Math.hypot(pill.x - pill.from.x, pill.y - pill.from.y) > PILL_H ? <line x1={pill.from.x} y1={pill.from.y} x2={pill.x} y2={pill.y} stroke="var(--ink-3)" strokeWidth="1" strokeDasharray="2 2" /> : null}
            <rect x={pill.x - pill.w / 2} y={pill.y - PILL_H / 2} width={pill.w} height={PILL_H} rx="11" fill="var(--surface)" stroke="var(--border)" />
            <text x={pill.x} y={pill.y + 4} fontSize={labelSize(11, width)} fontWeight="700" textAnchor="middle" fill="var(--gold-text)">{annotation.text}</text>
          </g>
        )}
      </svg>
    </ChartFrame>
  );
}
